import { apiClient } from "./api";

/**
 * REST read service for chat sessions and history.
 *
 * Replaces the legacy WebSocket `listSessions` / `loadHistory` / `deleteSession`
 * actions. Includes a localStorage stale-while-revalidate cache so the UI can
 * paint instantly from cache, then reconcile with the network response.
 *
 * Cache keys:
 *   anna_sessions_cache              -> last sessions list
 *   anna_history_<sessionId>         -> first page of a session's history
 */

const SESSIONS_CACHE_KEY = "anna_sessions_cache";
const HISTORY_CACHE_PREFIX = "anna_history_";
const DEFAULT_LIMIT = 10;

function _readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function _writeCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota or serialization error — caching is best-effort.
  }
}

// --- Sessions ---------------------------------------------------------------

/** Synchronously read the cached sessions list (for instant first paint). */
export function getCachedSessions() {
  const cached = _readCache(SESSIONS_CACHE_KEY);
  return Array.isArray(cached) ? cached : [];
}

/** Fetch the sessions list from the REST API and refresh the cache. */
export async function fetchSessions() {
  const data = await apiClient.get("/chat/sessions");
  const sessions = Array.isArray(data?.sessions) ? data.sessions : [];
  _writeCache(SESSIONS_CACHE_KEY, sessions);
  return sessions;
}

// --- History ----------------------------------------------------------------

/** Synchronously read the cached first page of a session's history. */
export function getCachedHistory(sessionId) {
  if (!sessionId) return null;
  return _readCache(`${HISTORY_CACHE_PREFIX}${sessionId}`);
}

/**
 * Fetch one page of history.
 *
 * @param {string} sessionId  Session to load. If falsy, loads the whole timeline.
 * @param {object} [opts]
 * @param {number} [opts.limit=10]   Page size.
 * @param {string} [opts.before]     SK cursor for older-page pagination.
 * @returns {Promise<{messages: Array, hasMore: boolean, cursor: string|null}>}
 */
export async function fetchHistory(sessionId, { limit = DEFAULT_LIMIT, before } = {}) {
  const params = new URLSearchParams();
  if (sessionId) params.set("sessionId", sessionId);
  params.set("limit", String(limit));
  if (before) params.set("before", before);

  const data = await apiClient.get(`/chat/history?${params.toString()}`);
  const result = {
    messages: Array.isArray(data?.messages) ? data.messages : [],
    hasMore: Boolean(data?.hasMore),
    cursor: data?.cursor ?? null,
  };

  // Only cache the first page (no `before` cursor) so the cache mirrors the
  // initial paint for this session.
  if (sessionId && !before) {
    _writeCache(`${HISTORY_CACHE_PREFIX}${sessionId}`, result);
  }
  return result;
}

// --- Delete -----------------------------------------------------------------

/** Delete a session and its messages, and evict it from the cache. */
export async function deleteSession(sessionId) {
  if (!sessionId) return;
  await apiClient.delete(`/chat/sessions/${encodeURIComponent(sessionId)}`);

  // Evict from caches.
  try {
    localStorage.removeItem(`${HISTORY_CACHE_PREFIX}${sessionId}`);
    const sessions = getCachedSessions().filter((s) => s.id !== sessionId);
    _writeCache(SESSIONS_CACHE_KEY, sessions);
  } catch {
    // best-effort
  }
}

export const chatApi = {
  getCachedSessions,
  fetchSessions,
  getCachedHistory,
  fetchHistory,
  deleteSession,
};

export default chatApi;
